import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, CheckCheck, CheckCircle2, XCircle, RotateCcw, Loader2, ClipboardCheck, Scale, ChevronRight, CalendarCheck, CalendarClock, AlarmClock, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '../context/NotificationCenterContext';
import { useNotification } from '../context/NotificationContext';

// Per-type visual language. The decision is the whole point of the notification,
// so it gets a colour (green approved / red denied / blue revised) rather than a
// word the eye has to read. Read rows keep the same hue at low intensity: the
// type stays legible in the list, it just stops competing for attention.
const TYPE_CONFIG = {
  review_approved: {
    icon: CheckCircle2,
    rail: 'bg-emerald-500',
    tile: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    tileRead: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-500/70 dark:text-emerald-400/50',
    dot: 'bg-emerald-500'
  },
  review_denied: {
    icon: XCircle,
    rail: 'bg-red-500',
    tile: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    tileRead: 'bg-red-50 dark:bg-red-900/10 text-red-500/70 dark:text-red-400/50',
    dot: 'bg-red-500'
  },
  review_rereviewed: {
    icon: RotateCcw,
    rail: 'bg-sky-500',
    tile: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
    tileRead: 'bg-sky-50 dark:bg-sky-900/10 text-sky-500/70 dark:text-sky-400/50',
    dot: 'bg-sky-500'
  },
  // Coaching (2026-11-03) — one violet family, told apart by the icon.
  coaching_scheduled: {
    icon: CalendarCheck,
    rail: 'bg-violet-500',
    tile: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
    tileRead: 'bg-violet-50 dark:bg-violet-900/10 text-violet-500/70 dark:text-violet-400/50',
    dot: 'bg-violet-500'
  },
  coaching_rescheduled: {
    icon: CalendarClock,
    rail: 'bg-violet-500',
    tile: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
    tileRead: 'bg-violet-50 dark:bg-violet-900/10 text-violet-500/70 dark:text-violet-400/50',
    dot: 'bg-violet-500'
  },
  coaching_reminder: {
    icon: AlarmClock,
    rail: 'bg-amber-500',
    tile: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    tileRead: 'bg-amber-50 dark:bg-amber-900/10 text-amber-500/70 dark:text-amber-400/50',
    dot: 'bg-amber-500'
  },
  coaching_agent_done: {
    icon: GraduationCap,
    rail: 'bg-emerald-500',
    tile: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    tileRead: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-500/70 dark:text-emerald-400/50',
    dot: 'bg-emerald-500'
  },
  // Appeal decisions — the review decision colours, the appeals icon.
  appeal_approved: {
    icon: Scale,
    rail: 'bg-emerald-500',
    tile: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    tileRead: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-500/70 dark:text-emerald-400/50',
    dot: 'bg-emerald-500'
  },
  appeal_denied: {
    icon: Scale,
    rail: 'bg-red-500',
    tile: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    tileRead: 'bg-red-50 dark:bg-red-900/10 text-red-500/70 dark:text-red-400/50',
    dot: 'bg-red-500'
  }
};

// Work WAITING on the viewer, as opposed to the stored rows below it, which
// are things that already happened to them. These used to be two full-width
// banners stacked above the page content on every QA Manager tab
// (ReviewNotificationBanner / AppealsNotificationBanner, deleted with this
// change): they pushed the actual page down by ~150px, and they duplicated
// what the Review and Appeals tab badges in the same header already say.
//
// They are deliberately NOT Notification rows. A row is one event, addressed
// to one person, kept forever; this is a LIVE COUNT of a shared queue. Writing
// a row per ticket entering review would put one entry per pending ticket in
// every reviewer's inbox — hundreds a week against a collection that holds
// under a thousand rows in its whole history — and the count would go stale
// the moment somebody else worked the queue. Rendering the number the context
// already polls costs nothing, is never stale, and disappears on its own when
// the queue empties.
//
// Colours are the ones the deleted banners used, so the thing people are
// looking for still looks like itself.
const QUEUE_CONFIG = {
  review: {
    icon: ClipboardCheck,
    rail: 'bg-amber-500',
    tile: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
  },
  appeals: {
    icon: Scale,
    rail: 'bg-violet-500',
    tile: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
  }
};

// A type the backend starts sending before this file catches up must still
// render a readable row instead of throwing on an undefined config.
const NEUTRAL_CONFIG = {
  icon: Bell,
  rail: 'bg-gray-400 dark:bg-neutral-600',
  tile: 'bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400',
  tileRead: 'bg-gray-50 dark:bg-neutral-800/50 text-gray-400 dark:text-neutral-500',
  dot: 'bg-gray-400 dark:bg-neutral-500'
};

// Local day-label / relative-time helpers rather than SlackUI's exported
// dayLabel: that one is pinned to Europe/Belgrade, while every other surface
// (Chat MessageList, GraderTimelineTab) labels days in the viewer's own zone.
// A notification about your own ticket has to agree with the clock in front of you.
const dayKey = (value) => new Date(value).toDateString();

const dayLabel = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dayKey(date) === dayKey(today)) return 'Today';
  if (dayKey(date) === dayKey(yesterday)) return 'Yesterday';

  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    // The year only earns its place once it is no longer obvious.
    ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {})
  });
};

const relativeTime = (value) => {
  const mins = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
};

// Hover title — the relative stamp is the glanceable one, this is the precise one.
const exactTime = (value) => {
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '';
  }
};

/**
 * @param {Array} queues - live queue items to surface above the stored
 *   notifications: { kind: 'review'|'appeals', count, title, body, link }.
 *   The CALLER decides who sees which — it holds the capability flags — so
 *   this component never has to know the QA permission model. Empty by
 *   default, which is what every non-QA mount gets.
 * @param {Function} renderTrigger - ({ open, onToggle, unreadCount, buttonRef })
 *   → the trigger element. Given (the left rail, AppSidebar), the panel opens
 *   beside the trigger in a portal instead of under a header button.
 */
const NotificationBell = ({ queues = [], renderTrigger = null }) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMore,
    markRead,
    markAllRead,
    refresh
  } = useNotificationCenter();
  // OS-permission state, for the in-panel enable row below.
  const { isSupported, permission, requestPermission } = useNotification();

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  // Rail placement: fixed, to the right of the trigger and bottom-aligned with
  // it (the bell sits at the foot of the rail), clamped to the viewport.
  const onRail = !!renderTrigger;
  const [railPos, setRailPos] = useState(null);

  // Relative timestamps go stale in an open panel — a "Just now" that still
  // says "Just now" ten minutes later is worse than no timestamp at all.
  // Re-render once a minute, but only while the panel is actually open.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNowTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [open]);

  // Close on outside click. mousedown (not click) so the panel is gone before
  // whatever was clicked behind it reacts — same as ZenMoveSettingsPopover.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      // The rail panel lives in a portal, outside rootRef.
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape. No focus trap: the rows are ordinary buttons in document
  // order, so Tab already behaves. All Escape owes a keyboard user is the
  // trigger back, so the next Tab resumes in the header instead of the page top.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Newest first (the API already sorts that way) collapsed into day buckets.
  // The label is deliberately NOT memoized with the group — it is computed at
  // render time so a panel left open across midnight relabels itself.
  const groups = useMemo(() => {
    const out = [];
    (notifications || []).forEach((n) => {
      const key = dayKey(n.createdAt);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(n);
      else out.push({ key, date: n.createdAt, items: [n] });
    });
    return out;
  }, [notifications]);

  const handleToggle = () => {
    const next = !open;
    if (next && onRail && buttonRef.current) {
      // Beside the rail itself, not the button inside it.
      const edge = (buttonRef.current.closest('aside') || buttonRef.current).getBoundingClientRect().right;
      const r = buttonRef.current.getBoundingClientRect();
      const width = Math.min(380, window.innerWidth - 24);
      setRailPos({
        left: Math.max(12, Math.min(edge + 8, window.innerWidth - width - 12)),
        bottom: Math.max(12, window.innerHeight - r.bottom),
        width
      });
    }
    setOpen(next);
    // Opening is the cheapest reconciliation point we get: a tab that was
    // asleep or offline missed its socket events, and the badge would stay
    // wrong until a reload.
    if (next) refresh();
  };

  const handleRowClick = (notification) => {
    if (!notification.isRead) markRead(notification._id);
    // Close before navigating — a panel left hanging over the destination
    // route reads as a rendering bug.
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  // The panel itself; the rail renders it in a portal beside the trigger.
  const panel = (
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            style={onRail && railPos ? { position: 'fixed', left: railPos.left, bottom: railPos.bottom, width: railPos.width } : undefined}
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            role="dialog"
            aria-label="Notifications"
            // Right-aligned and width-capped to the viewport: on a phone the
            // panel is 12px in from each edge (the header's own px-3 gutter),
            // so it can never push the page sideways. That math assumes the
            // bell is the RIGHTMOST item in the header cluster — anchor it to
            // the left of the Search/Throwback buttons instead and the mobile
            // panel hangs off the left edge by their combined width.
            className={`${onRail
              ? 'z-[60] origin-bottom-left'
              : 'absolute top-full right-0 mt-2 z-50 w-[calc(100vw-1.5rem)] sm:w-[380px] origin-top-right'} max-h-[70vh] flex flex-col overflow-hidden bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-lg`}
          >
            {/* Pinned head */}
            <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-neutral-800">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</span>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">
                    {unreadCount} new
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors whitespace-nowrap"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            {/* The permanent way in. The banner on the QA pages can be snoozed or
                dismissed for good, and a browser will never ask on its own —
                requestPermission has to come from a user gesture — so without a
                durable affordance "Later" would silently mean "never".
                'denied' is a separate case on purpose: requestPermission() is a
                no-op once the user has refused at browser level, so offering a
                button there would just look broken. Tell them where the switch
                actually is instead. */}
            {isSupported && permission !== 'granted' && (
              <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20">
                <Bell className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                {permission === 'denied' ? (
                  <p className="flex-1 text-[11px] leading-snug text-amber-800 dark:text-amber-300">
                    Notifications are blocked for this site. Allow them in your browser's
                    site settings to get alerted about your coaching sessions, appeals and reviews.
                  </p>
                ) : (
                  <>
                    <p className="flex-1 text-[11px] leading-snug text-amber-800 dark:text-amber-300">
                      Get alerted about your coaching sessions, appeals and reviews.
                    </p>
                    <button
                      type="button"
                      onClick={() => requestPermission()}
                      className="px-2 py-1 text-[11px] font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      Enable
                    </button>
                  </>
                )}
              </div>
            )}

            {/* overscroll-contain so flicking the list on a phone doesn't drag
                the page behind it once the list bottoms out. */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {/* Inside the scroll area, not pinned above it: the panel is
                  height-capped, and the whole reason these moved here was that
                  they were eating height somewhere else. First thing you see on
                  open, scrolls away when you go looking for older rows. */}
              {queues.length > 0 && (
                <div>
                  <div className="sticky top-0 z-10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-neutral-500 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-neutral-800/70">
                    Needs your decision
                  </div>
                  {queues.map((q) => {
                    const config = QUEUE_CONFIG[q.kind] || NEUTRAL_CONFIG;
                    const Icon = config.icon;
                    return (
                      <button
                        key={q.kind}
                        type="button"
                        onClick={() => { setOpen(false); navigate(q.link); }}
                        className="w-full flex items-stretch gap-2 pl-1.5 pr-2 py-2.5 text-left border-b border-gray-100 dark:border-neutral-800/60 hover:bg-gray-50 dark:hover:bg-neutral-800/40 transition-colors"
                      >
                        <span className={`w-0.5 rounded-full flex-shrink-0 ${config.rail}`} aria-hidden="true" />
                        <span className={`w-8 h-8 mt-0.5 rounded-lg flex items-center justify-center flex-shrink-0 ${config.tile}`}>
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {q.title}
                          </span>
                          <span className="mt-0.5 block text-xs line-clamp-2 text-gray-500 dark:text-neutral-400">
                            {q.body}
                          </span>
                        </span>
                        <ChevronRight className="w-4 h-4 mt-2 flex-shrink-0 text-gray-300 dark:text-neutral-600" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              )}
              {loading && groups.length === 0 ? (
                <div className="p-2 space-y-1">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex items-start gap-3 px-2 py-2.5 animate-pulse">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-neutral-800 flex-shrink-0" />
                      <div className="flex-1 space-y-1.5 pt-1">
                        <div className="h-2.5 w-2/3 rounded bg-gray-100 dark:bg-neutral-800" />
                        <div className="h-2 w-5/6 rounded bg-gray-100 dark:bg-neutral-800" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : groups.length === 0 ? (
                /* "All caught up" is a claim about the whole panel, so it is
                   only honest when the queue section above is empty too —
                   otherwise it sits directly under "34 tickets pending" and
                   contradicts it. With work waiting, this shrinks to a note
                   about the stored rows only. */
                queues.length > 0 ? (
                  <p className="px-3 py-4 text-xs text-center text-gray-400 dark:text-neutral-500">
                    No review decisions on your own tickets yet.
                  </p>
                ) : (
                  <div className="px-4 py-10 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-50 dark:bg-neutral-800/60 border border-gray-200 dark:border-neutral-800 flex items-center justify-center">
                      <BellOff className="w-4 h-4 text-gray-300 dark:text-neutral-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 dark:text-neutral-300">You're all caught up</p>
                    <p className="mt-1 text-xs text-gray-400 dark:text-neutral-500">
                      Coaching sessions, appeal decisions and reviews show up here.
                    </p>
                  </div>
                )
              ) : (
                groups.map((group) => (
                  <div key={group.key}>
                    <div className="sticky top-0 z-10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-neutral-500 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-neutral-800/70">
                      {dayLabel(group.date)}
                    </div>
                    {group.items.map((n) => {
                      const config = TYPE_CONFIG[n.type] || NEUTRAL_CONFIG;
                      const Icon = config.icon;
                      const unread = !n.isRead;
                      return (
                        <button
                          key={n._id}
                          type="button"
                          onClick={() => handleRowClick(n)}
                          className={`w-full flex items-stretch gap-2 pl-1.5 pr-3 py-2.5 text-left border-b border-gray-100 dark:border-neutral-800/60 last:border-b-0 transition-colors ${
                            unread
                              ? 'bg-gray-50/80 dark:bg-neutral-800/40 hover:bg-gray-100 dark:hover:bg-neutral-800/70'
                              : 'hover:bg-gray-50 dark:hover:bg-neutral-800/40'
                          }`}
                        >
                          {/* Accent rail — carries the decision colour while the
                              row is unread, then gets out of the way. */}
                          <span
                            className={`w-0.5 rounded-full flex-shrink-0 ${unread ? config.rail : 'bg-transparent'}`}
                            aria-hidden="true"
                          />
                          <span className={`w-8 h-8 mt-0.5 rounded-lg flex items-center justify-center flex-shrink-0 ${unread ? config.tile : config.tileRead}`}>
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="flex items-start justify-between gap-2">
                              {/* Plain text only. title/body carry a grader's
                                  free-text note — never innerHTML. */}
                              <span className={`text-sm truncate ${unread ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-600 dark:text-neutral-400'}`}>
                                {n.title}
                              </span>
                              <span className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                <span
                                  className="text-[11px] text-gray-400 dark:text-neutral-500 tabular-nums"
                                  title={exactTime(n.createdAt)}
                                >
                                  {relativeTime(n.createdAt)}
                                </span>
                                {unread && (
                                  <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} aria-hidden="true" />
                                )}
                              </span>
                            </span>
                            {n.body && (
                              <span className={`mt-0.5 block text-xs line-clamp-2 ${unread ? 'text-gray-600 dark:text-neutral-300' : 'text-gray-400 dark:text-neutral-500'}`}>
                                {n.body}
                              </span>
                            )}
                            {(n.ticketId || n.triggeredByName) && (
                              <span className="mt-1 flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-neutral-500 min-w-0">
                                {n.ticketId && (
                                  <span className="px-1 py-0.5 font-mono rounded bg-gray-100 dark:bg-neutral-800 truncate max-w-[45%]">
                                    {n.ticketId}
                                  </span>
                                )}
                                {n.triggeredByName && <span className="truncate">by {n.triggeredByName}</span>}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Explicit load-more rather than scroll-triggered paging: the panel
                is short, and a button can't double-fire on a momentum scroll. */}
            {hasMore && groups.length > 0 && (
              <div className="flex-shrink-0 border-t border-gray-200 dark:border-neutral-800 p-1.5">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
                >
                  {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {loading ? 'Loading…' : 'Load older'}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
  );

  return (
    <div className="relative" ref={rootRef}>
      {/* Trigger — same border/bg/height language as the Search and Throwback
          buttons beside it, minus the label since a bell needs no caption. The
          rail passes its own (renderTrigger). */}
      {renderTrigger ? renderTrigger({ open, onToggle: handleToggle, unreadCount, buttonRef }) : (
        <motion.button
          ref={buttonRef}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleToggle}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          className={`relative flex items-center px-2 py-1.5 text-sm border rounded-lg transition-colors ${
            open
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
              : 'text-gray-600 dark:text-neutral-400 bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700'
          }`}
          title="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            // ring matches the header shell (bg-gray-50 / dark:bg-neutral-950) so
            // the badge reads as a cutout rather than a sticker.
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold ring-2 ring-gray-50 dark:ring-neutral-950">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </motion.button>
      )}

      {onRail ? createPortal(panel, document.body) : panel}
    </div>
  );
};

export default NotificationBell;
